import React, { useEffect, useRef, useState } from 'react';
import { SkeletonBlock, SkeletonLine, SkeletonContent, } from '@one-thd/sui-atomic-components';
import { track, setPageInfo } from '../../../utils/analytics';
import { ERR_GENERIC } from '../../../constants/response-error-messages';
import { ERROR_CODES } from '../../../constants/custom-errors';
import { INSTANT_CHECKOUT_ADDRESS_UPDATE_SUCCESS, INSTANT_CHECKOUT_ENROLLMENT_SUCCESS, INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS, } from '../../../constants/response-success-messages';
import { useInstantCheckout } from '../../../hooks/use-instant-checkout';
import { SetUpInstantCheckout } from '../setup-instant-checkout/setup-instant-checkout';
import { useAddresses } from '../../../hooks/use-addresses';
import { usePayments } from '../../../hooks/use-payments';
import { CardType } from '../../../constants/payments';
import { InstantCheckoutAlert } from '../instant-checkout-alert/instant-checkout-alert';
import { TAlertStatus, TAlertType, } from '../../../models/common/alert-messages';
import { getPaymentModalsVersion, getSiteKey } from '../../../utils/general';
import { ExperienceFlows } from '../../../contexts/instant-checkout-context';
import { EnrolledSummary } from '../enrolled-summary/enrolled-summary';
import { setStartEnrollmentAnalytics, setSuccessfullyEnrolledAnalytics, setICSummaryPageViewAnalytics, } from '../../../utils/instant-checkout-analytics';
import { INSTANT_CHECKOUT_OVERLAY } from '../../../constants/analytics';
export const InstantCheckoutContent = () => {
    const { isEnrolled, alertMessage, accountData, isLoading, getProfileData, setAlertMessage, setIsLoading, setIsEnrolled, } = useInstantCheckout();
    const { getAddresses, defaultCheckoutAddress, addresses } = useAddresses();
    const { getPaymentCards, defaultCheckoutPayment, creditCards } = usePayments();
    const [wasUnenrolledDuringSession, setWasUnenrolledDuringSession] = useState(false);
    const iframeRef = useRef(null);
    const [isNewEnrollment, setIsNewEnrollment] = useState(false);
    const [isInitiallyLoaded, setIsInitiallyLoaded] = useState(false);
    const [currentDrawerExperience, setCurrentDrawerExperience] = useState(null);
    const [ICDataComplete, setICDataComplete] = useState(false);
    useEffect(() => {
        getICPageData();
        setICSummaryPageViewAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const handlePostMessageEvent = (event) => {
            var _a, _b;
            const { data } = event;
            if (!data)
                return;
            if (data.loaded && currentDrawerExperience) {
                (_b = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage({
                    event: 'start-experience',
                    siteKey: getSiteKey(false),
                    experienceProps: window === null || window === void 0 ? void 0 : window.__EXPERIENCE_PROPS__,
                    experienceContext: window === null || window === void 0 ? void 0 : window.__EXPERIENCE_CONTEXT__,
                    instantCheckoutContext: {
                        isNewEnrollment,
                        currentExperience: currentDrawerExperience,
                        creditCards: creditCards === null || creditCards === void 0 ? void 0 : creditCards.response,
                        addresses: addresses === null || addresses === void 0 ? void 0 : addresses.response,
                        accountData,
                    },
                });
            }
            if (data.isEnrolled || data.isReEnrolled) {
                setAlertMessage({
                    alertType: TAlertType.TOP,
                    alertStatus: TAlertStatus.SUCCESS,
                    text: INSTANT_CHECKOUT_ENROLLMENT_SUCCESS.message,
                });
                setSuccessfullyEnrolledAnalytics();
                setIsEnrolled(true);
                getICPageData();
            }
            if (data.isNewPaymentSelected || data.isEditPaymentSuccessful) {
                setAlertMessage({
                    alertType: TAlertType.IC_PAYMENT_UPDATE,
                    alertStatus: TAlertStatus.SUCCESS,
                    text: INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS.message,
                });
                getICPageData();
            }
            if (data.isNewAddressSelected || data.isEditAddressSuccessful) {
                setAlertMessage({
                    alertType: TAlertType.IC_ADDRESS_UPDATE,
                    alertStatus: TAlertStatus.SUCCESS,
                    text: INSTANT_CHECKOUT_ADDRESS_UPDATE_SUCCESS.message,
                });
                getICPageData();
            }
            if (data.closeDrawer) {
                setCurrentDrawerExperience(null);
            }
        };
        window.addEventListener('message', handlePostMessageEvent, false);
        return () => {
            window.removeEventListener('message', handlePostMessageEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, currentDrawerExperience, addresses]);
    const getICPageData = () => {
        setIsLoading(true);
        setICDataComplete(false);
        return Promise.all([
            getProfileData(),
            getAddressesWrapper(),
            getPaymentCards(CardType.CREDIT_CARD),
        ])
            .catch((error) => {
            var _a, _b, _c, _d;
            setAlertMessage({
                alertType: TAlertType.GENERAL,
                alertStatus: TAlertStatus.ERROR,
                text: ERR_GENERIC.message,
            });
            const code = (_d = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.errorCode;
            setPageInfo('error', 'page', 'instant checkout page', ERR_GENERIC.message, code || 'n/a');
        })
            .finally(() => {
            setIsLoading(false);
            setICDataComplete(true);
            setIsInitiallyLoaded(true);
        });
    };
    const getAddressesWrapper = () => {
        return getAddresses({ page: 0, pageSize: 0 }).catch((error) => {
            if (error.message === ERROR_CODES.NO_SAVED_ADR_CODE) {
                return;
            }
            throw error;
        });
    };
    const addOrSelectAddress = () => {
        if (addresses && addresses.addresses.length > 0) {
            setCurrentDrawerExperience(ExperienceFlows.SELECT_ADDRESS);
            track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout select delivery address',
            });
        }
        else {
            setCurrentDrawerExperience(ExperienceFlows.ADD_ADDRESS);
            track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout add delivery address',
            });
        }
    };
    const handleStartSetUp = () => {
        setIsNewEnrollment(true);
        setStartEnrollmentAnalytics();
        addOrSelectAddress();
    };
    const showContent = (alertMessage === null || alertMessage === void 0 ? void 0 : alertMessage.alertType) !== TAlertType.GENERAL && isInitiallyLoaded;
    const content = !wasUnenrolledDuringSession &&
        !isEnrolled &&
        !defaultCheckoutPayment &&
        !defaultCheckoutAddress ? (React.createElement(SetUpInstantCheckout, { handleStartSetUp: handleStartSetUp })) : (React.createElement(EnrolledSummary, { ICDataComplete: ICDataComplete, onEditDeliveryAddress: () => {
            track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout edit delivery address',
            });
            setCurrentDrawerExperience(ExperienceFlows.EDIT_ADDRESS);
        }, onChangeDeliveryAddress: () => {
            track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout select delivery address',
            });
            setCurrentDrawerExperience(ExperienceFlows.SELECT_ADDRESS);
        }, onChangeHowYouPay: () => {
            track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout select payment method',
            });
            setCurrentDrawerExperience(ExperienceFlows.SELECT_PAYMENT);
        }, onEditPaymentCard: () => {
            track(INSTANT_CHECKOUT_OVERLAY, {
                value: 'instant checkout edit payment method',
            });
            setCurrentDrawerExperience(ExperienceFlows.EDIT_PAYMENT);
        }, onHandleSetUpAddress: () => {
            addOrSelectAddress();
        }, onHandleSetUpPayment: () => {
            setIsNewEnrollment(true);
            if (creditCards && creditCards.paymentCards.length > 0) {
                setCurrentDrawerExperience(ExperienceFlows.SELECT_PAYMENT);
            }
            else {
                setCurrentDrawerExperience(ExperienceFlows.ADD_PAYMENT);
            }
        }, handleWasUnenrolledDuringSession: setWasUnenrolledDuringSession, handleReEnrollment: () => setCurrentDrawerExperience(ExperienceFlows.RE_ENROLLMENT) }));
    const paymentModalsVersion = getPaymentModalsVersion();
    const iframeSrc = `/account/payment-ui/${paymentModalsVersion}/dist/client/instant-checkout.html`;
    // local dev url
    // const iframeSrc = '/modals/instant-checkout.html';
    return (React.createElement(React.Fragment, null,
        (alertMessage === null || alertMessage === void 0 ? void 0 : alertMessage.alertType) === TAlertType.GENERAL && (React.createElement(InstantCheckoutAlert, null)),
        showContent && content,
        !showContent && (React.createElement(SkeletonContent, { disablePadding: true },
            React.createElement(SkeletonBlock, { height: 20 }),
            React.createElement(SkeletonLine, { variant: "multi", numberOfLines: 3 }))),
        currentDrawerExperience && paymentModalsVersion && (React.createElement("iframe", { ref: (el) => {
                iframeRef.current = el;
            }, title: "Instant Checkout Modal", src: iframeSrc, className: "sui-fixed sui-z-[900] sui-top-0 sui-bottom-0 sui-left-0 sui-right-0 sui-w-screen sui-h-screen", "data-testid": "instant-checkout-modal" }))));
};
